/**
 * CSS Performance Manager
 * Gerencia carregamento de CSS crítico vs não-crítico
 */

class CSSPerformanceManager {
    constructor() {
        this.criticalCSS = new Set();
        this.deferredCSS = new Set();
    }

    /**
     * Carregar CSS não-crítico de forma assíncrona
     * @param {string} href - URL do arquivo CSS
     * @param {string} id - ID único para o link
     * @param {number} delay - Delay antes de carregar (ms)
     */
    loadDeferredCSS(href, id = null, delay = 0) {
        return new Promise((resolve) => {
            setTimeout(() => {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = href;
                link.media = 'print'; // Carrega como print primeiro

                if (id) {
                    link.id = id;
                    this.deferredCSS.add(id);
                }

                link.onload = () => {
                    // Mudar media para all após carregamento
                    link.media = 'all';
                    resolve();
                };

                link.onerror = () => {
                    console.error(`Erro ao carregar CSS: ${href}`);
                    resolve();
                };

                document.head.appendChild(link);
            }, delay);
        });
    }

    /**
     * Carregar múltiplos CSS de forma escalonada
     */
    loadDeferredCSSBatch(cssArray, baseDelay = 0) {
        return Promise.all(
            cssArray.map((item, index) => {
                const { href, id, delay = baseDelay + (index * 100) } =
                    typeof item === 'string' ? { href: item } : item;
                return this.loadDeferredCSS(href, id, delay);
            })
        );
    }

    /**
     * Injetar CSS crítico inline (deve ser chamado no head)
     */
    injectCriticalCSS(cssText) {
        const style = document.createElement('style');
        style.textContent = cssText;
        style.setAttribute('data-critical', 'true');
        document.head.insertBefore(style, document.head.firstChild);
    }

    /**
     * Observar elementos com animações e carregar CSS quando visível
     */
    observeAnimationElements() {
        const animationObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const element = entry.target;

                    // Se elemento tem classe com animação, carregar CSS
                    if (element.hasAttribute('data-animation')) {
                        const animationFile = element.getAttribute('data-animation');
                        this.loadDeferredCSS(animationFile);
                    }

                    animationObserver.unobserve(element);
                }
            });
        }, {
            threshold: 0.1,
            rootMargin: '50px'
        });

        // Observar todos os elementos com animação
        document.querySelectorAll('[data-animation]').forEach(el => {
            animationObserver.observe(el);
        });
    }

    /**
     * Prefetch de CSS (será carregado em background)
     */
    prefetchCSS(href) {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = href;
        document.head.appendChild(link);
    }

    /**
     * Otimizar font-face loading
     */
    optimizeFontLoading() {
        if ('fonts' in document) {
            document.fonts.ready.then(() => {
                document.documentElement.classList.add('fonts-loaded');
            });
        }
    }

    /**
     * Monitorar performance de CSS
     */
    measureCSSPerformance() {
        const navigation = performance.getEntriesByType('navigation')[0];
        const styleStart = performance.getEntriesByType('measure')
            .filter(m => m.name.includes('style'));

        return {
            totalCSSSize: styleStart.reduce((acc, s) => acc + (s.duration || 0), 0),
            cssLinks: document.querySelectorAll('link[rel="stylesheet"]').length,
            inlineStyles: document.querySelectorAll('style').length,
            criticalCSS: this.criticalCSS.size,
            deferredCSS: this.deferredCSS.size
        };
    }

    /**
     * Reportar Web Vitals relacionados a CSS
     */
    reportCSSMetrics() {
        const metrics = {
            FCP: 0, // First Contentful Paint
            LCP: 0  // Largest Contentful Paint
        };

        // Usar PerformanceObserver se disponível
        if (typeof PerformanceObserver !== 'undefined') {
            try {
                const observer = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        if (entry.name === 'first-contentful-paint') {
                            metrics.FCP = entry.startTime;
                        }
                        if (entry.entryType === 'largest-contentful-paint') {
                            metrics.LCP = entry.renderTime || entry.loadTime;
                        }
                    }
                });

                observer.observe({ entryTypes: ['paint', 'largest-contentful-paint'] });
            } catch (e) {
                console.debug('PerformanceObserver não suportado');
            }
        }

        return metrics;
    }
}

// Inicializar globalmente
window.cssManager = new CSSPerformanceManager();

// Otimizar font loading automaticamente
window.cssManager.optimizeFontLoading();

// Observar elementos com animações
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.cssManager.observeAnimationElements();
    });
} else {
    window.cssManager.observeAnimationElements();
}
